import { FormEvent, useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { ViewableQuestion } from '@/types/global';
import { ResponseButton } from './ResponseButton';

interface OtherDialogProps extends ResponseOtherFieldProps {
  open: boolean;
  questionLabel: ViewableQuestion['label'];
  onClose: () => void;
}

const OtherDialog = ({
  value,
  label,
  questionLabel,
  onChange,
  open,
  onClose,
}: OtherDialogProps) => {
  const [localValue, setLocalValue] = useState(value ?? '');

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (localValue && localValue.trim() !== '') {
        onChange(localValue);
      } else {
        onChange(undefined);
      }
      onClose();
    },
    [localValue, onChange, onClose],
  );

  const handleCancelButton = () => {
    setLocalValue(value ?? '');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <div className="flex flex-col items-center justify-center gap-1 p-6">
          {questionLabel && (
            <span className="pl-1 w-full font-semibold mb-2">
              {questionLabel}
            </span>
          )}
          <p className="text-sm pl-1 text-gray-600 w-full">{label}</p>
          <div className="w-full">
            <Input
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex items-center gap-6 [@media(max-width:26rem)]:gap-2 pt-2">
            <Button
              variant="ctaSecondary"
              size="xs"
              type="button"
              onClick={handleCancelButton}
            >
              Passer
            </Button>
            <Button onClick={onSubmit} variant="ctaPrimary" size="xs">
              Suivant
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface ResponseOtherFieldProps {
  value?: string;
  label: string;
  questionLabel: ViewableQuestion['label'];
  multiselect?: boolean;
  onChange: (value: string | undefined) => void;
}

export const ResponseOtherField = ({
  value,
  label,
  questionLabel,
  multiselect,
  onChange,
}: ResponseOtherFieldProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const selected = Boolean(value?.length);

  return (
    <>
      <ResponseButton
        label={value ?? label}
        selected={selected}
        onChange={() => setDialogOpen(true)}
        multiselect={multiselect}
        freeform
      />
      <OtherDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        value={value}
        label={label}
        questionLabel={questionLabel}
        onChange={onChange}
      />
    </>
  );
};
